import { Fragment, useState } from 'react';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, Area, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ReferenceLine, ReferenceDot,
} from 'recharts';
import { aedStr, ilsStr, eurStr, usdStr, computeCashFlow } from '../lib/analysisEngine';
import { CHART_COLORS as C, compact } from '../lib/uiFormat';
import { useI18n } from '../lib/i18n';

// Cash-flow colour language (kept consistent with the other charts):
//   money in = green, money out = red, per-period net = blue line,
//   cumulative net position = amber area, handover = teal reference line.
const IN = C.payment;        // #16a34a green
const OUT = '#dc2626';       // red
const NET = C.value;         // #2563eb blue
const CUM = C.invested;      // #f59e0b amber
const mkHandoverRef = (value) => ({ value, position: 'insideTopRight', fontSize: 10, fill: C.handover, fontWeight: 600 });

function signed(n) {
  const v = Math.round(Number(n) || 0);
  return (v > 0 ? '+' : '') + aedStr(v);
}

// ---- amount cell with an itemised hover tooltip (fees / cost breakdown) ----
function ItemAmount({ amount, items, prefix = '−' }) {
  const { tMilestone } = useI18n();
  const [open, setOpen] = useState(false);
  const showItems = items && items.length > 1;
  if (!amount) return <span className="text-gray-300">—</span>;
  return (
    <span className="relative inline-flex items-center gap-1 justify-end"
      onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <span className="tabular-nums">{prefix}{aedStr(amount)}</span>
      {showItems && <span className="text-[9px] text-gray-300 cursor-help print:hidden">ⓘ</span>}
      {open && showItems && (
        <span className="absolute z-40 bottom-full right-0 mb-1 w-max max-w-[240px] bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-left print:hidden">
          {items.map((it, i) => (
            <span key={i} className="flex justify-between gap-4 text-[11px] text-gray-600 whitespace-nowrap">
              <span>{it.isFee ? '🧾 ' : ''}{tMilestone(it.label)}{it.isPost ? ' (PHPP)' : ''}</span>
              <span className="tabular-nums font-medium text-gray-800">{aedStr(it.amount)}</span>
            </span>
          ))}
        </span>
      )}
    </span>
  );
}

// ---- summary strip: peak cash requirement / break-even / end position ----
function SummaryStrip({ cf, ils, eur, usd }) {
  const { t } = useI18n();
  const be = cf.breakEvenReached
    ? cf.breakEven.label
    : t('beyond_plus_y', { y: cf.maturityYears });
  const cards = [
    {
      label: t('peak_cash_requirement'), hint: t('peak_hint'),
      aed: cf.peak.magnitude, ils: cf.peak.magnitude * ils, eur: cf.peak.magnitude * eur, usd: cf.peak.magnitude * usd, sub: t('on_x', { x: cf.peak.label }), accent: 'text-gray-900',
    },
    {
      label: t('cash_break_even'), hint: t('be_hint'),
      big: be, sub: cf.breakEvenReached ? t('cumulative_turns_positive') : t('not_within_horizon'), accent: cf.breakEvenReached ? 'text-green-600' : 'text-amber-600',
    },
    {
      label: t('cumulative_position_plus_y', { y: cf.maturityYears }), hint: t('horizon_end_hint'),
      aed: cf.endCumulative, ils: cf.endCumulative * ils, eur: cf.endCumulative * eur, usd: cf.endCumulative * usd, sub: cf.endCumulative >= 0 ? t('cash_positive') : t('still_recovering'), accent: cf.endCumulative >= 0 ? 'text-green-600' : 'text-red-600',
    },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
      {cards.map((c, i) => (
        <div key={i} className="rounded-xl border border-black/[0.06] bg-gray-50 p-3.5">
          <p className="text-[11px] uppercase tracking-wide text-gray-400 font-medium leading-tight">{c.label}</p>
          {c.big
            ? <p className={`text-xl font-bold tabular-nums ${c.accent} mt-1`}>{c.big}</p>
            : <>
                <p className={`text-xl font-bold tabular-nums ${c.accent} mt-1`}>{aedStr(c.aed)}</p>
                <p className="text-[11px] text-gray-400 tabular-nums">{ilsStr(c.ils)}</p>
                {eur != null && <p className="text-[11px] text-gray-400 tabular-nums">{eurStr(c.eur)}</p>}
                {usd != null && <p className="text-[11px] text-gray-400 tabular-nums">{usdStr(c.usd)}</p>}
              </>}
          <p className="text-[11px] text-gray-400 mt-0.5">{c.sub}</p>
        </div>
      ))}
    </div>
  );
}

// ---- chart tooltip ----
function CashTooltip({ active, payload, label, ils, eur, usd }) {
  const { t } = useI18n();
  if (!active || !payload || !payload.length) return null;
  const row = payload[0]?.payload || {};
  const line = (name, val, color) => (
    <div className="flex items-center gap-2 mb-1 last:mb-0">
      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: color }} />
      <span className="text-gray-500">{name}</span>
      <span className="ml-auto text-right leading-tight">
        <span className="font-medium text-gray-800 block tabular-nums">{signed(val)}</span>
        <span className="text-[10px] text-gray-400 block tabular-nums">{ilsStr(val * ils)}</span>
        {eur != null && <span className="text-[10px] text-gray-400 block tabular-nums">{eurStr(val * eur)}</span>}
        {usd != null && <span className="text-[10px] text-gray-400 block tabular-nums">{usdStr(val * usd)}</span>}
      </span>
    </div>
  );
  return (
    <div className="bg-white/95 backdrop-blur border border-gray-200 rounded-xl shadow-lg px-3 py-2 text-xs min-w-[190px]">
      <p className="font-semibold text-gray-700 mb-1.5">{label}</p>
      {line(t('money_in_rent'), row.moneyIn, IN)}
      {line(t('money_out'), -row.moneyOutMag, OUT)}
      {line(t('net_cash_flow'), row.net, NET)}
      <div className="border-t border-black/[0.06] mt-1 pt-1">{line(t('cumulative_position'), row.cumulative, CUM)}</div>
      {row.altNet != null && line(t('net_other_strategy'), row.altNet, '#94a3b8')}
    </div>
  );
}

function CashChart({ cf, ils, eur, usd, altCf, height = 300 }) {
  const { t, dir } = useI18n();
  const handoverRef = mkHandoverRef(t('handover'));
  const altByLabel = altCf ? new Map(altCf._rows.map(y => [y.label, y.net])) : null;
  // chart uses whatever granularity cf holds (built by the caller from quarters/yearly)
  const data = cf._rows.map(r => ({
    label: r.label,
    moneyIn: Math.round(r.rentIn),
    moneyOutMag: Math.round(r.payments + r.ownership),   // magnitude; drawn negative
    moneyOut: -Math.round(r.payments + r.ownership),
    net: Math.round(r.net),
    cumulative: Math.round(r.cumulative),
    altNet: altByLabel && altByLabel.has(r.label) ? Math.round(altByLabel.get(r.label)) : null,
  }));
  const beLabel = cf.breakEvenReached
    ? (cf._mode === 'yearly' ? cf.breakEvenYear?.label
      : cf._mode === 'monthly' ? cf.breakEven?.monthLabel
      : cf.breakEven?.quarterLabel)
    : null;
  const handoverX = cf._mode === 'yearly' ? String(cf.handoverYear)
    : cf._mode === 'monthly' ? cf.handoverMonthLabel
    : cf.handoverLabel;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={data} margin={{ top: 10, right: 8, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" angle={-25} textAnchor="end" height={52} />
        <YAxis yAxisId="flow" tick={{ fontSize: 10 }} tickFormatter={compact} width={44} />
        <YAxis yAxisId="cum" orientation="right" tick={{ fontSize: 10 }} tickFormatter={compact} width={46} />
        <Tooltip content={<CashTooltip ils={ils} eur={eur} usd={usd} />} />
        <Legend wrapperStyle={{ fontSize: 11 }} align={dir === 'rtl' ? 'right' : 'left'} />
        <ReferenceLine yAxisId="flow" y={0} stroke="#e5e7eb" />
        {handoverX != null && <ReferenceLine yAxisId="flow" x={handoverX} stroke={C.handover} strokeDasharray="4 3" label={handoverRef} />}
        <Area yAxisId="cum" type="monotone" dataKey="cumulative" name={t('cumulative_position')} stroke={CUM} strokeWidth={2} fill={CUM} fillOpacity={0.12} isAnimationActive={false} />
        <Bar yAxisId="flow" dataKey="moneyIn" name={t('money_in')} fill={IN} isAnimationActive={false} radius={[2, 2, 0, 0]} />
        <Bar yAxisId="flow" dataKey="moneyOut" name={t('money_out')} fill={OUT} isAnimationActive={false} radius={[0, 0, 2, 2]} />
        <Line yAxisId="flow" type="monotone" dataKey="net" name={t('net_cash_flow')} stroke={NET} strokeWidth={2} dot={false} isAnimationActive={false} />
        {altCf && <Line yAxisId="flow" type="monotone" dataKey="altNet" name={t('net_other_strategy')} stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="5 4" dot={false} isAnimationActive={false} connectNulls />}
        {beLabel && <ReferenceDot yAxisId="cum" x={beLabel} y={0} r={5} fill={CUM} stroke="#fff" strokeWidth={2}
          label={{ value: t('cash_be_x', { x: beLabel }), position: 'top', fontSize: 10, fill: CUM, fontWeight: 600 }} />}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

// ---- table row (desktop) ----
function Row({ r, ils, eur, usd, bold = false, indent = false }) {
  const { t } = useI18n();
  const netColor = r.net >= 0 ? 'text-green-600' : 'text-red-600';
  return (
    <tr className={`border-b border-gray-50 ${bold ? 'bg-gray-50 font-semibold' : ''}`}>
      <td className={`py-2 pr-3 whitespace-nowrap tabular-nums ${indent ? 'pl-4 text-gray-500' : 'text-gray-800'}`}>
        {r.label}
        {r.hasHandover && <span className="ml-2 text-[9px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">{t('badge_handover')}</span>}
        {r.isHandover && !r.hasHandover && <span className="ml-2 text-[9px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">{t('badge_handover')}</span>}
      </td>
      <td className="py-2 px-3 text-right text-red-600 tabular-nums"><ItemAmount amount={r.payments} items={r.paymentItems} ils={ils} eur={eur} usd={usd} /></td>
      <td className="py-2 px-3 text-right text-red-600 tabular-nums"><ItemAmount amount={r.ownership} items={r.ownershipItems} ils={ils} eur={eur} usd={usd} /></td>
      <td className="py-2 px-3 text-right text-green-600 tabular-nums">{r.rentIn ? '+' + aedStr(r.rentIn) : <span className="text-gray-300">—</span>}</td>
      <td className={`py-2 px-3 text-right tabular-nums ${netColor}`}>{signed(r.net)}</td>
      <td className={`py-2 pl-3 text-right tabular-nums font-medium ${r.cumulative >= 0 ? 'text-green-700' : 'text-gray-700'}`}>{aedStr(r.cumulative)}</td>
    </tr>
  );
}

function DesktopTable({ cf, mode, ils, eur, usd }) {
  const { t } = useI18n();
  return (
    <div className="hidden md:block overflow-x-auto">
      <table className="w-full text-sm min-w-[680px]">
        <thead>
          <tr className="text-left text-gray-400 border-b border-black/[0.06]">
            <th className="py-2 pr-3 font-medium">{t('period')}</th>
            <th className="py-2 px-3 font-medium text-right">{t('payments')}</th>
            <th className="py-2 px-3 font-medium text-right">{t('ownership')}</th>
            <th className="py-2 px-3 font-medium text-right">{t('rent_in')}</th>
            <th className="py-2 px-3 font-medium text-right">{t('net')}</th>
            <th className="py-2 pl-3 font-medium text-right">{t('cumulative')}</th>
          </tr>
        </thead>
        <tbody>
          {mode === 'yearly'
            ? cf.yearly.map((y, i) => <Row key={i} r={y} ils={ils} eur={eur} usd={usd} />)
            : mode === 'monthly'
              ? cf.monthly.map((m, i) => <Row key={i} r={m} ils={ils} eur={eur} usd={usd} />)
              : cf.yearly.map((y) => (
                  <Fragment key={y.year}>
                    {cf.quarters.filter(q => q.year === y.year).map((q, i) => <Row key={i} r={q} ils={ils} eur={eur} usd={usd} indent />)}
                    <Row r={{ ...y, label: t('x_total', { x: y.year }) }} ils={ils} eur={eur} usd={usd} bold />
                  </Fragment>
                ))}
        </tbody>
      </table>
    </div>
  );
}

// ---- mobile stacked period cards ----
function MobileCards({ cf, mode, ils, eur, usd }) {
  const { t } = useI18n();
  const rows = mode === 'yearly' ? cf.yearly : mode === 'monthly' ? cf.monthly : cf.quarters;
  return (
    <div className="md:hidden space-y-2">
      {rows.map((r, i) => {
        const netColor = r.net >= 0 ? 'text-green-600' : 'text-red-600';
        return (
          <div key={i} className={`rounded-xl border p-3 ${r.isHandover || r.hasHandover ? 'border-teal-200 bg-teal-50/40' : 'border-black/[0.06] bg-white'}`}>
            <div className="flex justify-between items-center">
              <p className="font-medium text-gray-800 tabular-nums">{r.label}
                {(r.isHandover || r.hasHandover) && <span className="ml-1.5 text-[9px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">{t('badge_handover')}</span>}
              </p>
              <p className={`font-bold tabular-nums ${netColor}`}>{signed(r.net)}</p>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-2 text-[11px]">
              <div><span className="text-gray-400 block">{t('payments')}</span><span className="tabular-nums text-red-600">{r.payments ? '−' + aedStr(r.payments) : '—'}</span></div>
              <div><span className="text-gray-400 block">{t('ownership')}</span><span className="tabular-nums text-red-600">{r.ownership ? '−' + aedStr(r.ownership) : '—'}</span></div>
              <div><span className="text-gray-400 block">{t('rent_in')}</span><span className="tabular-nums text-green-600">{r.rentIn ? '+' + aedStr(r.rentIn) : '—'}</span></div>
            </div>
            <div className="flex justify-between items-center mt-2 pt-2 border-t border-black/[0.06] text-[11px]">
              <span className="text-gray-400">{t('cumulative_position')}</span>
              <span className={`tabular-nums font-medium ${r.cumulative >= 0 ? 'text-green-700' : 'text-gray-700'}`}>{aedStr(r.cumulative)} · {ilsStr(r.cumulative * ils)}{eur != null && <> · {eurStr(r.cumulative * eur)}</>}{usd != null && <> · {usdStr(r.cumulative * usd)}</>}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Prepare the chart-view slice (rows + mode metadata) without mutating the source cf.
function withRows(cf, mode) {
  const _rows = mode === 'yearly' ? cf.yearly : mode === 'monthly' ? cf.monthly : cf.quarters;
  return { ...cf, _rows, _mode: mode };
}

// ===== main section (Advanced full + Lite compact) — used by AnalysisReport =====
// props: a (analysis), ils, view ('lite'|'advanced'), altAnalysis (compare: other strategy)
export default function CashFlowSection({ a, ils, eur, usd, view = 'advanced', altAnalysis = null }) {
  const { t } = useI18n();
  const lite = view === 'lite';
  // Yearly is the default in Lite / on mobile; Advanced defaults to quarterly.
  const [mode, setMode] = useState(lite ? 'yearly' : 'quarterly');
  const cf = computeCashFlow(a);
  if (!cf) return null;
  const altCf = altAnalysis ? withRows(computeCashFlow(altAnalysis), mode) : null;
  const chartCf = withRows(cf, mode);

  return (
    <section className="bg-white rounded-[1.75rem] p-4 md:p-5 shadow-premium border border-black/[0.06] mb-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h3 className="font-semibold text-gray-800">{t('cash_flow')}</h3>
        <div className="inline-flex bg-gray-50 border border-gray-200 rounded-xl p-1">
          {/* Yearly is the client-facing default; Monthly is internal-only (Advanced). */}
          {[['quarterly', t('quarterly')], ['yearly', t('yearly')], ...(lite ? [] : [['monthly', t('monthly')]])].map(([v, lbl]) => (
            <button key={v} onClick={() => setMode(v)}
              className={`px-3 py-1 rounded-lg text-xs font-medium transition ${mode === v ? 'bg-dark text-white' : 'text-gray-500 hover:text-gray-700'}`}>{lbl}</button>
          ))}
        </div>
      </div>
      <p className="text-[11px] text-gray-400 mb-4">
        {t('cf_note', {
          rev: a.strategy === 'str' ? t('gross_str_revenue') : t('rent_lc'),
          alt: altCf ? t('cf_alt_note') : '',
        })}
      </p>

      <SummaryStrip cf={cf} ils={ils} eur={eur} usd={usd} />

      <div className={lite ? '' : 'mb-5'}>
        <CashChart cf={chartCf} ils={ils} eur={eur} usd={usd} altCf={altCf} height={lite ? 260 : 300} />
      </div>

      {/* period-by-period breakdown — Advanced only; Lite already tells the story via
          the summary strip + chart above */}
      {!lite && (<>
        <DesktopTable cf={cf} mode={mode} ils={ils} eur={eur} usd={usd} />
        <MobileCards cf={cf} mode={mode} ils={ils} eur={eur} usd={usd} />
      </>)}

      {!cf.breakEvenReached && (
        <p className="mt-3 text-[11px] text-amber-600 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
          {t('cf_not_reached', { y: cf.maturityYears, x: aedStr(cf.endCumulative) })}
        </p>
      )}
    </section>
  );
}

// ===== static, yearly-only version for the phone PDF (MobileReport) =====
export function CashFlowMobile({ a, ils, eur, usd }) {
  const { t } = useI18n();
  const cf = computeCashFlow(a);
  if (!cf) return null;
  const chartCf = withRows(cf, 'yearly');
  return (
    <div className="rounded-[1.75rem] bg-white border border-black/[0.06] p-4">
      <h3 className="font-semibold text-gray-800 text-[15px] mb-1">{t('cash_flow')}</h3>
      <p className="text-[12px] text-gray-400 mb-3">{t('cf_mobile_note')}</p>
      <div className="grid grid-cols-1 gap-2 mb-3">
        <div className="rounded-xl bg-gray-50 border border-black/[0.06] p-3">
          <p className="text-[12px] text-gray-500">{t('peak_cash_requirement')}</p>
          <p className="text-[20px] font-bold text-gray-900 tabular-nums">{aedStr(cf.peak.magnitude)}</p>
          <p className="text-[11px] text-gray-400 tabular-nums">{cf.peak.label} · {ilsStr(cf.peak.magnitude * ils)}{eur != null && <> · {eurStr(cf.peak.magnitude * eur)}</>}{usd != null && <> · {usdStr(cf.peak.magnitude * usd)}</>}</p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-xl bg-gray-50 border border-black/[0.06] p-3">
            <p className="text-[12px] text-gray-500">{t('cash_break_even')}</p>
            <p className={`text-[16px] font-bold tabular-nums ${cf.breakEvenReached ? 'text-green-600' : 'text-amber-600'}`}>{cf.breakEvenReached ? cf.breakEvenYear.label : t('gt_plus_y', { y: cf.maturityYears })}</p>
          </div>
          <div className="rounded-xl bg-gray-50 border border-black/[0.06] p-3">
            <p className="text-[12px] text-gray-500">{t('end_position_plus_y', { y: cf.maturityYears })}</p>
            <p className={`text-[16px] font-bold tabular-nums ${cf.endCumulative >= 0 ? 'text-green-600' : 'text-red-600'}`}>{aedStr(cf.endCumulative)}</p>
          </div>
        </div>
      </div>
      <CashChart cf={chartCf} ils={ils} eur={eur} usd={usd} height={280} />
      <div className="mt-3 space-y-1.5">
        {cf.yearly.map((y, i) => (
          <div key={i} className="flex items-center justify-between text-[12px] border-b border-gray-50 pb-1.5">
            <span className="text-gray-700 font-medium w-12">{y.label}</span>
            <span className="tabular-nums text-red-600 w-20 text-right">{y.payments + y.ownership ? '−' + aedStr(y.payments + y.ownership) : '—'}</span>
            <span className="tabular-nums text-green-600 w-16 text-right">{y.rentIn ? '+' + aedStr(y.rentIn) : '—'}</span>
            <span className={`tabular-nums font-medium w-20 text-right ${y.cumulative >= 0 ? 'text-green-700' : 'text-gray-700'}`}>{aedStr(y.cumulative)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
